import apiService from "@/api-service";
import { OvereenkomstHouder } from "@/models/ovk";
import { RootState } from "@/store";
import { OvksState, ovksActions } from "@/store/reducers/ovks";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button, Col, Container, Form, Row, Table, Dropdown, InputGroup } from "react-bootstrap";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MdDownload, MdArrowUpward, MdArrowDownward } from "react-icons/md";
import useLoading from "@/hooks/loading.hook";
import useAPIDownload from "@/hooks/download.hook";
import OvkCSVExportModal from "@/components/ovk-csv-export-modal/ovk-csv-export-modal";
import FilterInfoButton from "@/components/common/filter-info-button";

type OvkSortKey = 'ovk_nummer' | 'ovk_naam' | 'bestekken';

interface SortConfig {
    key: OvkSortKey;
    direction: 'asc' | 'desc';
}

const SortableHeader: React.FC<{
    children: React.ReactNode;
    sortKey: OvkSortKey;
    currentSort: SortConfig | null;
    onSort: (key: OvkSortKey) => void;
    style?: React.CSSProperties;
}> = ({ children, sortKey, currentSort, onSort, style }) => {
    const isActive = currentSort?.key === sortKey;
    const direction = isActive ? currentSort.direction : null;
    return (
        <th
            style={{ ...style, cursor: 'pointer', userSelect: 'none', position: 'relative' }}
            onClick={() => onSort(sortKey)}
            className={isActive ? 'table-active' : ''}
        >
            <div className="d-flex align-items-center justify-content-between">
                <span>{children}</span>
                <span style={{ marginLeft: '5px', opacity: isActive ? 1 : 0.3 }}>
                    {direction === 'asc' ? <MdArrowUpward size={14} /> : <MdArrowDownward size={14} />}
                </span>
            </div>
        </th>
    );
};

interface Props {
    ovks: OvksState
}

function mapStateToProps(state: RootState): Props {
    return {
        ovks: state.ovks
    };
}

const OvkList = (props: Props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const loader = useLoading();
    const downloadFromAPI = useAPIDownload();
    
    const [csvExportModalShown, setCsvExportModalShown] = useState<boolean>(false);
    const [pageInput, setPageInput] = useState<string>("");
    const [filterVisible, setFilterVisible] = useState<boolean>(true);
    const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

    const handleSort = useCallback((key: OvkSortKey) => {
        setSortConfig(prev =>
            prev?.key === key && prev.direction === 'asc'
                ? { key, direction: 'desc' }
                : { key, direction: 'asc' }
        );
        setActivePage(1);
    }, []);
    
    useEffect(() => {
        if (!props.ovks.loaded) {
            apiService.get<OvereenkomstHouder[]>("/ovk/")
                .then((response) => {
                    dispatch(ovksActions.set_data(response.data))
                }, console.error);
        }
    }, []);

    const pageSize: number = 10;
    const [ activePage, setActivePage ] = useState<number>(1);

    const filteredOvks = (props.ovks.filter_text ?? "").length > 0 ? props.ovks.data.filter(ovk => {
        return ovk.ovk_nummer == props.ovks.filter_text || ovk.ovk_naam.toLowerCase().includes(props.ovks.filter_text ?? "");
    }) : props.ovks.data;

    if (filteredOvks.length < pageSize * (activePage - 1)) {
        setActivePage(Math.ceil(filteredOvks.length / pageSize));
    }

    const sortedOvks = useMemo(() => {
        if (!sortConfig) return filteredOvks;
        return [...filteredOvks].sort((a, b) => {
            let aVal: string | number;
            let bVal: string | number;
            if (sortConfig.key === 'bestekken') {
                aVal = a.bestekken.length;
                bVal = b.bestekken.length;
            } else if (sortConfig.key === 'ovk_nummer') {
                aVal = parseInt(a.ovk_nummer ?? '0', 10) || 0;
                bVal = parseInt(b.ovk_nummer ?? '0', 10) || 0;
            } else {
                aVal = (a[sortConfig.key] ?? '').toLowerCase();
                bVal = (b[sortConfig.key] ?? '').toLowerCase();
            }
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredOvks, sortConfig]);

    const shownOvks = sortedOvks.slice(
        (activePage - 1) * pageSize,
        activePage * pageSize
    );

    const getCurrentFilterParams = () => {
        return {
            filter_text: props.ovks.filter_text || undefined
        };
    };

    const download = (format: 'csv' | 'xlsx'): void => {
        loader.show("Export voorbereiden...");
        
        downloadFromAPI(
            { 
                method: 'post', 
                path: '/ovk/export', 
                data: { 
                    search_params: getCurrentFilterParams(), 
                    format: format 
                } 
            },
            `overeenkomsthouders_export.${format}`,
            {
                finally: loader.hide
            }
        );
    };

    const totalPages = useMemo(() => Math.ceil(filteredOvks.length / pageSize), [filteredOvks.length, pageSize]);
    
    const handleGoToPage = useCallback(() => {
        const pageNum = parseInt(pageInput);
        if (pageNum >= 1 && pageNum <= totalPages) {
            setActivePage(pageNum);
            setPageInput("");
        }
    }, [pageInput, totalPages]);

    const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleGoToPage();
        }
    }, [handleGoToPage]);

    return (
        <div className="pt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 d-flex align-items-center gap-1">
                    Zoekfilters
                    <FilterInfoButton
                        id="ovk-filter-uitleg"
                        text="Zoekt gelijktijdig op naam en OVK-nummer."
                    />
                </h5>
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: '13px' }}>
                        {filteredOvks.length} {filteredOvks.length === 1 ? 'resultaat' : 'resultaten'}
                    </span>
                    <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <MdDownload className="me-1" />Export
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end">
                            <Dropdown.Item onClick={() => setCsvExportModalShown(true)}>
                                Aangepaste export
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.ItemText className="small text-muted">Snelle export (alle kolommen):</Dropdown.ItemText>
                            {filteredOvks.length > 0 && filteredOvks.length <= 1000 ? (
                                <>
                                    <Dropdown.Item onClick={() => download('csv')}>CSV</Dropdown.Item>
                                    <Dropdown.Item onClick={() => download('xlsx')}>Excel</Dropdown.Item>
                                </>
                            ) : filteredOvks.length > 1000 ? (
                                <Dropdown.ItemText className="small text-muted">
                                    Gebruik aangepaste export voor &gt;1000 resultaten.
                                </Dropdown.ItemText>
                            ) : (
                                <Dropdown.ItemText className="small text-muted">Geen resultaten.</Dropdown.ItemText>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setFilterVisible(!filterVisible)}
                    >
                        {filterVisible ? 'Filter verbergen' : 'Filter tonen'}
                    </Button>
                </div>
            </div>

            <div>
                {filterVisible && (
                    <Container fluid>
                        <Row className="g-3 align-items-end">
                            <Col md={8} lg={6}>
                                <label className="form-label mb-1">Zoekterm</label>
                                <Form.Control
                                    id="ovk-filter-text"
                                    value={props.ovks.filter_text ?? ""}
                                    type="text"
                                    placeholder="Typ een zoekterm..."
                                    onChange={(e) => dispatch(ovksActions.set_filter_text(e.target.value))}
                                />
                            </Col>
                            </Row>
                    </Container>
                )}
            </div>

            <Table className="mt-3" size="sm" striped hover>
                <thead>
                    <tr>
                        <SortableHeader sortKey="ovk_nummer" currentSort={sortConfig} onSort={handleSort} style={{ width: '10%' }}>
                            Nummer
                        </SortableHeader>
                        <SortableHeader sortKey="ovk_naam" currentSort={sortConfig} onSort={handleSort}>
                            Naam
                        </SortableHeader>
                        <SortableHeader sortKey="bestekken" currentSort={sortConfig} onSort={handleSort} style={{ width: '15%' }}>
                            Aantal bestekken
                        </SortableHeader>
                    </tr>
                </thead>
                <tbody>
                    {
                        (shownOvks.length === 0) && (
                            <tr>
                                <td colSpan={3} align="center">
                                    Er zijn geen overeenkomsthouders die voldoen aan de zoekcriteria
                                </td>
                            </tr>
                        )
                    }
                    {
                        shownOvks.length > 0 && 
                        shownOvks
                            .map(ovk => {
                                return (
                                    <tr key={`ovk-id-${ovk.id}`} onClick={() => navigate(`/ovks/${ovk.id}/${ovk.ovk_nummer}/${ovk.ovk_naam}`)}>
                                        <td>{ovk.ovk_nummer}</td>
                                        <td>{ovk.ovk_naam}</td>
                                        <td>{ovk.bestekken.length}</td>
                                    </tr>
                                )
                            })
                    }
                </tbody>
            </Table>

            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3">
                <div style={{ flex: 1, minWidth: '300px' }}>
                    <PaginationControl 
                        page={activePage}
                        between={4}
                        total={filteredOvks.length}
                        limit={pageSize}
                        changePage={setActivePage}
                        ellipsis={1}
                    />
                </div>
                
                <div className="d-flex flex-column align-items-end">
                    <InputGroup size="sm" style={{ width: '100px' }}>
                        <Form.Control
                            type="number"
                            placeholder="Pagina"
                            value={pageInput}
                            onChange={(e) => setPageInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            min={1}
                            max={totalPages}
                        />
                        <Button 
                            variant="outline-secondary" 
                            onClick={handleGoToPage}
                            disabled={!pageInput}
                        >
                            Ga
                        </Button>
                    </InputGroup>
                    <small className="text-muted mt-1">
                        Pagina {activePage} van {totalPages}
                    </small>
                </div>
            </div>

            <OvkCSVExportModal
                show={csvExportModalShown}
                onHide={() => setCsvExportModalShown(false)}
                filterParams={getCurrentFilterParams()}
                totalCount={filteredOvks.length}
            />
        </div>
    )
}

export default connect(mapStateToProps)(OvkList);